import { JsonEditor, getNodeLabel } from "@/common";
import { useFullRelease } from "@/common/hooks_useFullRelease";
import FocusNode from "@/components/FocusNode/FocusNode";
import { useGetter } from "@/hooks/use-getter";
import AttributeContext from "@/pages/models/release/AttributeContext";
import ReleaseContextProvider from "@/pages/models/release/ReleaseContextProvider";
import Button from "@imminently/decisively-ui/Button";
import DevPanel from "@imminently/decisively-ui/DevPanel";
import Dialog from "@imminently/decisively-ui/Dialog";
import { DialogControls } from "@/ui/dialogs/DialogHeader";
import Icon from "@imminently/decisively-ui/Icon";
import type { DecisionGraph, GraphJson } from "@imminently/decisively-core";
import type { VisibleMask } from "@/components/GraphVisualisation/decisionGraphView";
import { last } from "lodash";
import type React from "react";
import { createContext, useContext, useState } from "react";

interface FocusModalContextType {
  openFocusModal: (node: any) => void;
  closeFocusModal: () => void;
}

const FocusModalContext = createContext<FocusModalContextType | null>(null);

export const useFocusModal = () => {
  const context = useContext(FocusModalContext);
  if (!context) {
    throw new Error("useFocusModal must be used within a FocusModalProvider");
  }
  return context;
};

const FocusModalContent = ({
  node,
  release,
  getGraph,
  visible,
  history,
  setHistory,
}: {
  node: any;
  release: any;
  getGraph: () => any;
  visible?: VisibleMask;
  history: any[];
  setHistory: (history: any[]) => void;
}) => {

  console.log("the node",node);
  return (
    <ReleaseContextProvider release={release}>
      <AttributeContext.Provider value={node}>
        <FocusNode
          node={node}
          graph={getGraph()}
          visible={visible}
          onNodeChange={(newNode: any) => {
            if (!newNode) return;
            if (history.length === 0 || newNode.id !== last(history).id) {
              setHistory([...history, newNode]);
            }
          }}
        />
      </AttributeContext.Provider>
    </ReleaseContextProvider>
  );
};

interface FocusModalProviderProps {
  children: React.ReactNode;
  /** A DecisionGraph, or a saved result's legacy JSON graph (FocusNode converts it). */
  graph: DecisionGraph | GraphJson;
  /** With a DecisionGraph: the ids in the view. */
  visible?: VisibleMask;
}

const FocusModalProvider: React.FC<FocusModalProviderProps> = ({ children, graph, visible }) => {
  const [modalNode, setModalNode] = useState<any | null>(null);
  const release = useFullRelease();
  const getGraph = useGetter(graph);
  const [history, setHistory] = useState<any[]>([]);
  const focusedNode = last(history) || modalNode;

  const openFocusModal = (node: any) => {
    setModalNode(node);
    setHistory([]);
  };

  const closeFocusModal = () => {
    setModalNode(null);
    setHistory([]);
  };

  return (
    <FocusModalContext.Provider value={{ openFocusModal, closeFocusModal }}>
      {children}
      <Dialog
        open={!!modalNode}
        onOpenChange={(open) => !open && closeFocusModal()}
      >
        <Dialog.Content
          style={{
            width: "calc(90vw - 2rem)",
            minHeight: 800,
            height: "calc(90vh - 2rem)",
            padding: 0,
          }}
        >
          <Dialog.Header className={"border-b"}>
            <Dialog.Title className={"flex items-center gap-2"}>
              {history.length > 1 ? (
                <Button
                  icon
                  label="Back"
                  onClick={() =>
                    setHistory((history) => {
                      const newHistory = [...history];
                      newHistory.pop();
                      return newHistory;
                    })
                  }
                >
                  <Icon name="go-back" />
                </Button>
              ) : null}
              {focusedNode ? getNodeLabel(focusedNode) : ""}
            </Dialog.Title>
            <DialogControls allowResize={true} />
          </Dialog.Header>
          {focusedNode && (
            <div
              style={{ height: "calc(100% - 60px)", overflow: "auto", position: "relative" }}
              className={"bg-white"}
            >
              <FocusModalContent
                history={history}
                setHistory={setHistory}
                node={focusedNode}
                release={release}
                getGraph={getGraph}
                visible={visible}
              />
              <DevPanel>
                <div style={{ width: 360, height: 320 }}>
                  <JsonEditor
                    value={JSON.stringify(focusedNode, null, 2)}
                    readOnly
                    withCopy
                  />
                </div>
              </DevPanel>
            </div>
          )}
        </Dialog.Content>
      </Dialog>
    </FocusModalContext.Provider>
  );
};

export default FocusModalProvider;
